import { useState, useEffect } from "react";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Default categories that come pre-configured
const DEFAULT_CATEGORIES = [
  "Electronics",
  "Furniture",
  "Office Supplies",
  "Raw Materials",
  "Finished Goods",
  "Work in Progress",
  "Packaging Materials",
  "Consumables",
  "Tools & Equipment",
  "Safety Equipment",
];

interface CategoryComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function CategoryCombobox({ value, onValueChange, disabled, className }: CategoryComboboxProps) {
  const [categories, setCategories] = useState<string[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  // Load categories from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("stockCategories");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCategories(parsed);
      } catch (error) {
        console.error("Failed to parse categories:", error);
        setCategories(DEFAULT_CATEGORIES);
        localStorage.setItem("stockCategories", JSON.stringify(DEFAULT_CATEGORIES));
      }
    } else {
      setCategories(DEFAULT_CATEGORIES);
      localStorage.setItem("stockCategories", JSON.stringify(DEFAULT_CATEGORIES));
    }
  }, []);

  const handleAddCategory = () => {
    const trimmed = newCategory.trim();
    if (!trimmed) {
      toast.error("Category name cannot be empty");
      return;
    }

    if (categories.includes(trimmed)) {
      toast.error("Category already exists");
      return;
    }

    const updated = [...categories, trimmed].sort();
    setCategories(updated);
    localStorage.setItem("stockCategories", JSON.stringify(updated));
    onValueChange(trimmed);
    setNewCategory("");
    setShowAddDialog(false);
    toast.success(`Category "${trimmed}" added`);
  };

  const categoryOptions: ComboboxOption[] = categories.map((cat) => ({
    value: cat,
    label: cat,
  }));

  return (
    <>
      <div className="flex gap-2">
        <div className="flex-1">
          <Combobox
            options={categoryOptions}
            value={value}
            onValueChange={onValueChange}
            placeholder="Select or search category..."
            searchPlaceholder="Search categories..."
            emptyText="No category found."
            disabled={disabled}
            className={className}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setShowAddDialog(true)}
          disabled={disabled}
          title="Add new category"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
            <DialogDescription>
              Create a custom category for organizing your stock items.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-category">Category Name</Label>
              <Input
                id="new-category"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g., Spare Parts, Chemicals, etc."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCategory();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory}>Add Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
